import type { DefectItemDefinition, InspectionPeriod } from '@/app/data/type'
import { ApiError } from '@/app/auth'

// 하자진단 항목편집(설정 > 입력항목) 공용 로직. 화면은 Setting/DefectItemTab.vue + Setting/DefectItem/*

export const DEFECT_ITEM_NAME_MAX = 100
export const DEFECT_REF_TEXT_MAX = 400
export const DEFECT_REF_MAX = 3

// 근거 이미지는 dev·운영 모두 zentrix/defect_refs/ 하나를 쓴다(GCS_ROOT_PATH 미사용, 2026-11-04 사용자 지시)
export const DEFECT_REF_BUCKET = 'homecheck-service'
const DEFECT_REF_DIR = 'zentrix/defect_refs'
const DEFECT_REF_IMAGE_MAX_EDGE = 2000

// 전유 POST = 공종 단위 정의 (공종 → 위치 → 하자내용 3컬럼)
export function isTradeKey(structure: string, period: InspectionPeriod | string): boolean {
  return structure === 'EXCLUSIVE' && period === 'POST'
}

// 하자발생내용 체크 칸 (부록 양식 section*.hwpx 라벨 순서). PRE·공용 POST 9칸, 전유 POST 13칸
export const CHECK_UNBUILT = 3
export const CHECK_BEFORE = 7
export const CHECK_AFTER = 8

interface CheckGroup {
  title: string
  items: { index: number; label: string }[]
}

const BASE_CHECK_GROUPS: CheckGroup[] = [
  {
    title: '하자 판정',
    items: [
      { index: 0, label: '기능상 하자' },
      { index: 1, label: '안전상 하자' },
      { index: 2, label: '미관상 하자' },
    ],
  },
  {
    title: '발생 원인',
    items: [
      { index: 3, label: '미시공 하자' },
      { index: 4, label: '설계상 하자' },
      { index: 5, label: '변경시공 하자' },
      { index: 6, label: '감리상 하자' },
    ],
  },
  {
    title: '발생 시기',
    items: [
      { index: 7, label: '사용검사 이전 발생' },
      { index: 8, label: '사용검사 이후 발생' },
    ],
  },
]

const TRADE_CHECK_GROUPS: CheckGroup[] = [
  ...BASE_CHECK_GROUPS,
  {
    title: '보수 가능 여부',
    items: [
      { index: 9, label: '보수 가능함' },
      { index: 10, label: '보수 불가능' },
    ],
  },
  {
    title: '하자의 중요성',
    items: [
      { index: 11, label: '중요한 하자' },
      { index: 12, label: '중요하지 않은 하자' },
    ],
  },
]

export function getCheckGroups(structure: string, period: InspectionPeriod | string): CheckGroup[] {
  return isTradeKey(structure, period) ? TRADE_CHECK_GROUPS : BASE_CHECK_GROUPS
}

export function checksLength(structure: string, period: InspectionPeriod | string): number {
  return isTradeKey(structure, period) ? 13 : 9
}

// 등록 시 체크 초기값: '미시공' → 미시공 하자, 전/후 → 발생시기. 공종(전유 POST)도 같은 규칙(서버 기본값은 공종에 체크를 안 넣어 UI가 명시)
export function initialChecks(
  structure: string,
  period: InspectionPeriod | string,
  name: string,
): (0 | 1)[] {
  const checks = new Array<0 | 1>(checksLength(structure, period)).fill(0)
  if (name.includes('미시공')) checks[CHECK_UNBUILT] = 1
  checks[period === 'PRE' ? CHECK_BEFORE : CHECK_AFTER] = 1
  return checks
}

// 길이가 어긋난 checks(옛 데이터)는 칸 수에 맞춰 자르거나 0으로 채운다
export function normalizeChecks(
  checks: unknown,
  structure: string,
  period: InspectionPeriod | string,
): (0 | 1)[] {
  const length = checksLength(structure, period)
  const source = Array.isArray(checks) ? checks : []
  return Array.from({ length }, (_, i) => (source[i] === 1 ? 1 : 0))
}

// 부록 '1. 설계도서 및 관련근거' 줄 (근거 문구 줄 = 근거 블록, 최대 3)
export function buildRefLines(texts: string[]): string[] {
  return texts.filter(text => text.trim()).map(text => `- ${text.trim()}`)
}

// 상세 미작성 경고 (목록 점 표시용). 빈 문자열이면 경고 없음
export function getDefinitionWarning(
  def: DefectItemDefinition | undefined,
  period: InspectionPeriod | string,
): string {
  if (!def) return '상세 정보 없음'
  if (period === 'PRE' && def.ref_ids.length === 0) return '관련근거 없음'
  if (period === 'POST' && !def.problem?.trim()) return '문제점 없음'
  if (!def.cost?.trim()) return '보수비 산정방법 없음'
  return ''
}

// 문구 자동완성 후보: 회사 전체 정의에서 distinct, 사용 빈도순
export function collectSuggestions(
  defs: DefectItemDefinition[],
  field: 'cost' | 'problem' | 'etc_note',
): string[] {
  const counts = new Map<string, number>()
  for (const def of defs) {
    const value = def[field]?.trim()
    if (!value) continue
    counts.set(value, (counts.get(value) ?? 0) + 1)
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([value]) => value)
}

// 드래그 순서 변경 (이름 기준)
export function reorderByName<T extends { name: string }>(
  list: T[],
  draggedName: string,
  targetName: string,
  position: 'top' | 'bottom',
): T[] {
  const newArr = [...list]
  const draggedIdx = newArr.findIndex(v => v.name === draggedName)
  const targetIdx = newArr.findIndex(v => v.name === targetName)
  if (draggedIdx === -1 || targetIdx === -1 || draggedIdx === targetIdx) return list
  const [removed] = newArr.splice(draggedIdx, 1)
  let insertIdx = targetIdx
  if (position === 'bottom') insertIdx += draggedIdx < targetIdx ? 0 : 1
  else insertIdx += draggedIdx < targetIdx ? -1 : 0
  newArr.splice(insertIdx, 0, removed)
  return newArr
}

// 서버 거절 메시지 (409 '같은 이름…' 등)를 토스트에 그대로 보여준다
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    const match = error.message.match(/^\[API ERROR\] (.*) at \S+$/)
    const message = match?.[1]?.trim()
    if (message && error.statusCode !== 500) return message
  }
  return fallback
}

function randomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

export function defectRefImagePath(companyId: number, ext: 'png' | 'jpg'): string {
  return `${DEFECT_REF_DIR}/${companyId}/${randomId()}.${ext}`
}

// 근거 이미지 준비: PNG/JPG 유지(webp 변환 안 함 - 스캔 글씨 보존), 긴 변 2000px 초과 시만 축소
export async function prepareRefImage(
  file: Blob,
): Promise<{ file: File; ext: 'png' | 'jpg'; width: number; height: number }> {
  const ext: 'png' | 'jpg' = file.type === 'image/jpeg' ? 'jpg' : 'png'
  const mime = ext === 'jpg' ? 'image/jpeg' : 'image/png'
  const url = URL.createObjectURL(file)
  try {
    const img = new Image()
    await new Promise<void>((resolve, reject) => {
      img.onload = (): void => resolve()
      img.onerror = (): void => reject(new Error('이미지를 읽을 수 없습니다.'))
      img.src = url
    })
    const width = img.naturalWidth
    const height = img.naturalHeight
    if (!width || !height) throw new Error('이미지 크기를 알 수 없습니다.')

    const scale = Math.min(1, DEFECT_REF_IMAGE_MAX_EDGE / Math.max(width, height))
    // 원본이 PNG/JPG이고 축소가 필요 없으면 바이트 그대로 올린다
    if (scale === 1 && (file.type === 'image/png' || file.type === 'image/jpeg')) {
      return { file: new File([file], `ref.${ext}`, { type: mime }), ext, width, height }
    }

    const outWidth = Math.max(1, Math.round(width * scale))
    const outHeight = Math.max(1, Math.round(height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = outWidth
    canvas.height = outHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('이미지를 변환할 수 없습니다.')
    if (ext === 'jpg') {
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, outWidth, outHeight)
    }
    ctx.drawImage(img, 0, 0, outWidth, outHeight)
    const blob = await new Promise<Blob | null>(resolve =>
      canvas.toBlob(resolve, mime, ext === 'jpg' ? 0.92 : undefined),
    )
    if (!blob) throw new Error('이미지를 변환할 수 없습니다.')
    return {
      file: new File([blob], `ref.${ext}`, { type: mime }),
      ext,
      width: outWidth,
      height: outHeight,
    }
  } finally {
    URL.revokeObjectURL(url)
  }
}
